import { Quat, Vec3 } from 'playcanvas';

import { CameraMotionController, type CameraMotionTargetSet, type CameraMotionTrackData } from './camera-motion-controller';
import { parseCameraMotionCsv } from './csv-camera-parser';
import { ElementType } from './element';
import { Events } from './events';
import { HelperPoint } from './helper-point';
import type { Vec3Tuple } from './math-types';
import { Scene } from './scene';

export type CameraMotionAxisPreset = 'zUpToYUp' | 'yUpKeep';

export type CameraMotionEulerOverrides = {
    /** Negate CSV pitch angle before conversion. */
    invertPitch?: boolean;

    /** Negate CSV roll angle before conversion. */
    invertRoll?: boolean;

    /** Negate CSV yaw angle before conversion. */
    invertYaw?: boolean;

    /** Swap CSV pitch and roll inputs. */
    swapPitchRoll?: boolean;
};

export type CameraMotionImportOptions = {
    axisPreset?: CameraMotionAxisPreset;
    eulerOverrides?: CameraMotionEulerOverrides;

    /**
     * Forward distance used to synthesize camera target from Euler angles.
     * Defaults to 10 units.
     */
    eulerForwardDistance?: number;

    /**
     * Frames-per-second used to convert CSV seconds into timeline frames.
     * Defaults to current timeline frameRate or 30.
     */
    fps?: number;

    /**
     * When true, the timeline length is extended to fit the imported motion.
     * Defaults to true.
     */
    extendTimeline?: boolean;
};

export type CameraMotionImportResult = {
    root: HelperPoint;
    body: HelperPoint;
    controller: CameraMotionController;
    warnings: string[];
    durationSec: number;
    framesRequired: number;
    fps: number;
};

const mapPosition = (p: Vec3Tuple, preset: CameraMotionAxisPreset): Vec3Tuple => {
    // CSV often arrives in right-handed Z-up coordinates.
    // PlayCanvas is right-handed Y-up, so rotate by X(-90deg): (x, y, z) -> (x, z, -y).
    if (preset === 'zUpToYUp') {
        return [p[0], p[2], -p[1]];
    }

    return [p[0], p[1], p[2]];
};

const getBasisQuat = (preset: CameraMotionAxisPreset) => {
    const q = new Quat();
    if (preset === 'zUpToYUp') {
        q.setFromAxisAngle(Vec3.RIGHT, -90);
    }
    return q;
};

const getEulerQuat = (
    pitchDeg: number,
    rollDeg: number,
    yawDeg: number,
    preset: CameraMotionAxisPreset,
    overrides: CameraMotionEulerOverrides
) => {
    const pitch0 = pitchDeg;
    const roll0 = rollDeg;
    const yaw0 = yawDeg;

    let pitch = overrides.swapPitchRoll ? roll0 : pitch0;
    let roll = overrides.swapPitchRoll ? pitch0 : roll0;
    const yaw = overrides.invertYaw ? -yaw0 : yaw0;

    if (overrides.invertPitch) pitch = -pitch;
    if (overrides.invertRoll) roll = -roll;

    const qCsv = new Quat().setFromEulerAngles(pitch, roll, yaw);
    const qBasis = getBasisQuat(preset);
    const qBasisInv = qBasis.clone().invert();

    const qTmp = new Quat().mul2(qBasis, qCsv);
    return qTmp.mul(qBasisInv).normalize();
};

const findNextCameraIndex = (scene: Scene) => {
    const helpers = scene.getElementsByType(ElementType.helper) as HelperPoint[];

    let maxFromName = 0;
    let attachedCount = 0;

    for (const helper of helpers) {
        if (!(helper as any).cameraMotionController) continue;

        attachedCount++;

        const match = helper.name.match(/^cam(\d+)_root$/i);
        if (!match) continue;

        const n = Number(match[1]);
        if (Number.isFinite(n) && n > maxFromName) {
            maxFromName = n;
        }
    }

    if (maxFromName > 0) {
        return maxFromName + 1;
    }

    return attachedCount + 1;
};

const ensureAtLeastOneKey = (track: CameraMotionTrackData) => {
    if (track.frames.length > 0) return;
    track.frames.push(0);
    track.positions.push(0, 0, 0);
    track.targets.push(0, 0, -1);
};

export async function importCameraMotionCsvText(
    text: string,
    scene: Scene,
    events: Events,
    options: CameraMotionImportOptions = {}
): Promise<CameraMotionImportResult | null> {
    const warnings: string[] = [];

    const axisPreset = options.axisPreset ?? 'zUpToYUp';
    const overrides = options.eulerOverrides ?? {};

    const timelineFps = events.invoke('timeline.frameRate');
    let fps: number;
    if (typeof options.fps === 'number' && Number.isFinite(options.fps) && options.fps > 0) {
        fps = options.fps;
    } else if (typeof timelineFps === 'number' && Number.isFinite(timelineFps) && timelineFps > 0) {
        fps = timelineFps;
    } else {
        fps = 30;
    }

    const parsed = parseCameraMotionCsv(text, { zeroTimeToFirstSample: true });
    warnings.push(...parsed.warnings);

    if (parsed.samples.length < 1) {
        warnings.push('CSV contained no usable samples.');
        console.warn(warnings.join('\n'));
        return null;
    }

    const firstWithPosition = parsed.samples.find(sample => !!sample.position);
    if (!firstWithPosition?.position) {
        warnings.push('No sample contains a valid camera position.');
        console.warn(warnings.join('\n'));
        return null;
    }

    const rootPosMapped = mapPosition(firstWithPosition.position, axisPreset);
    const rootWorldPos = new Vec3(rootPosMapped[0], rootPosMapped[1], rootPosMapped[2]);

    const camIndex = findNextCameraIndex(scene);
    const camPrefix = `cam${camIndex}`;

    // Important: establish logical hierarchy before any scene.add() calls so UI hierarchy listeners
    // observe correct parent/child relationships from first add event onward.
    const root = new HelperPoint({
        name: `${camPrefix}_root`,
        animated: false,
        visible: true
    });
    root.entity.setLocalPosition(rootWorldPos);

    const body = new HelperPoint({
        name: `${camPrefix}_body`,
        animated: true,
        visible: true,
        parentEntity: root.entity
    });
    body.parentElement = root;
    root.childElements.push(body);

    await scene.add(root);
    await scene.add(body);
    events.fire('hierarchy.changed');

    const track: CameraMotionTrackData = {
        frames: [],
        positions: [],
        targets: []
    };

    const targetDistanceRaw = options.eulerForwardDistance;
    const targetDistance = (typeof targetDistanceRaw === 'number' && Number.isFinite(targetDistanceRaw) && targetDistanceRaw > 0)
        ? targetDistanceRaw
        : 10;

    const posWorld = new Vec3();
    const targetWorld = new Vec3();
    const posLocal = new Vec3();
    const targetLocal = new Vec3();
    const forward = new Vec3();

    let lastFrame = -Infinity;

    for (const sample of parsed.samples) {
        if (!sample.position) {
            warnings.push(`Row ${sample.sourceRow}: missing position; sample skipped.`);
            continue;
        }

        const frame = Math.round(sample.tSec * fps);
        if (!(frame > lastFrame)) {
            warnings.push(`Row ${sample.sourceRow}: duplicate/non-increasing rounded frame (${frame}); sample skipped.`);
            continue;
        }

        const pMapped = mapPosition(sample.position, axisPreset);
        posWorld.set(pMapped[0], pMapped[1], pMapped[2]);

        if (parsed.format === 'posTarget') {
            if (!sample.target) {
                warnings.push(`Row ${sample.sourceRow}: missing target for posTarget format; sample skipped.`);
                continue;
            }

            const tMapped = mapPosition(sample.target, axisPreset);
            targetWorld.set(tMapped[0], tMapped[1], tMapped[2]);
        } else {
            const pitch = sample.anglesDeg.pitch;
            const roll = sample.anglesDeg.roll;
            const yaw = sample.anglesDeg.yaw;

            if (pitch === null || roll === null || yaw === null) {
                warnings.push(`Row ${sample.sourceRow}: missing Euler angles for cogEuler format; sample skipped.`);
                continue;
            }

            const q = getEulerQuat(pitch, roll, yaw, axisPreset, overrides);
            q.transformVector(Vec3.FORWARD, forward);
            targetWorld.copy(posWorld).add(forward.mulScalar(targetDistance));
        }

        posLocal.copy(posWorld).sub(rootWorldPos);
        targetLocal.copy(targetWorld).sub(rootWorldPos);

        track.frames.push(frame);
        track.positions.push(posLocal.x, posLocal.y, posLocal.z);
        track.targets.push(targetLocal.x, targetLocal.y, targetLocal.z);

        lastFrame = frame;
    }

    ensureAtLeastOneKey(track);

    const durationSec = parsed.samples[parsed.samples.length - 1].tSec;
    const framesRequired = Math.max(1, Math.ceil(durationSec * fps) + 1);

    if (options.extendTimeline ?? true) {
        const currentFrames = events.invoke('timeline.frames');
        if (typeof currentFrames === 'number' && Number.isFinite(currentFrames) && framesRequired > currentFrames) {
            events.fire('timeline.setFrames', framesRequired);
        }
    }

    events.fire('timeline.setFrame', 0);

    const targets: CameraMotionTargetSet = {
        body: body.entity
    };

    const controller = new CameraMotionController(events, targets, track);

    (root as any).cameraMotionController = controller;
    (root as any).cameraMotionAnimatedHelpers = { body };

    scene.forceRender = true;

    return {
        root,
        body,
        controller,
        warnings,
        durationSec,
        framesRequired,
        fps
    };
}
